// npm install https://cdn.sheetjs.com/xlsx-0.20.3/xlsx-0.20.3.tgz
import * as XLSX from 'xlsx';

/**
 * @class XLSXImporter
 * @description
 * XLSXImporter 클래스는 엑셀 파일(xlsx, xls)을 읽고
 * string[][] 형태로 변환하여 콜백 함수로 전달하는 기능을 제공합니다.
 * SheetJS 라이브러리를 기반으로 동작합니다.
 * 
 * @see https://docs.sheetjs.com
 */
export class XLSXImporter {
    private file: File | null = null;

    constructor(file?: File) {
        if(file){
            this.file = file;
        }
    }

    /**
     * @method setFile
     * @description 엑셀 파일 set
     * @param file 입력받은 파일 객체
     */
    public setFile(file: File): void {
        this.file = file;
    }

    /**
     * @method import
     * @description 설정된 file을 string[][] 형태로 반환
     * @returns Promise<string[][]>
     */
    public async import(): Promise<string[][]> {

        if (!this.file) throw new Error("파일이 설정되지 않았습니다.");

        // 파일 읽기
        const data = await this.readFileAsArrayBuffer(this.file);
 
        const workbook = XLSX.read(data, { type: "array" });
        
        if (workbook.SheetNames.length === 0) {
            throw new Error("엑셀 파일에 시트가 없습니다.");
        }
        
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const rawData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: "" }) as any[][];
        
        if (!Array.isArray(rawData) || !Array.isArray(rawData[0])) {
            throw new Error("파일 형식이 올바르지 않습니다. 파일 형식을 확인해주세요.");
        }

        return this.stringifyData(rawData);
    };


    /**
     * @method readFileAsArrayBuffer
     * @description FileReader를 이용해 File을 ArrayBuffer로 반환
     * @param file 
     * @return Promise<ArrayBuffer>
     */
    private readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
    
            reader.onload = () => {
                if (reader.result instanceof ArrayBuffer) {
                    resolve(reader.result);
                } else {
                    reject(new Error("파일을 ArrayBuffer로 읽는 데 실패했습니다."));
                }
            };
    
            reader.onerror = () => {
                reject(reader.error || new Error("파일 읽기 실패"));
            };
    
            reader.readAsArrayBuffer(file);
        });
    }

    /**
     * @description 엑셀 시트 데이터를 모두 문자열로 변환 (undefined/null → "")
     * @param data 엑셀 시트 원본 데이터
     * @return string[][]
     */
    private stringifyData(data: any[][]): string[][] {
        //const maxLength = Math.max(...data.map(row => row.length));
        
        return data.map(row =>
            row.map(cell =>
                cell !== undefined && cell !== null ? String(cell) : ""
            )
        );
    }
}